import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../lib/ThemeContext';
import { SpinResult } from '../../types';
import { colorOptions } from '../../lib/SpinnerContext';
import { isToday, isYesterday, format } from 'date-fns';
import Modal from 'react-modal';

interface ResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: SpinResult[];
}

export const ResultsModal: React.FC<ResultsModalProps> = ({ isOpen, onClose, results }) => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();

  // Helper function to get proper spinner name
  const getSpinnerName = (result: SpinResult) => {
    // If spinner has a custom name, use it
    if (result.spinnerName && result.spinnerName.trim()) {
      return result.spinnerName;
    }
    
    // Default fallback
    return t('sidebar.results.untitled', 'Untitled');
  };

  // Helper function to get game type suffix
  const getGameTypeSuffix = (result: SpinResult) => {
    // If there's a game type (Fun Mode was active), show it
    if (result.gameType) {
      return `: ${t(`sidebar.games.${result.gameType}`, result.gameType)}`;
    }
    
    // For non-participants (dice/numbers/colors/binary), show "Just Spinner"
    if (['dice', 'numbers', 'random-numbers', 'colors', 'binary'].includes(result.type)) {
      return `: ${t('sidebar.games.justSpinner', 'Just Spinner')}`;
    }
    
    // For participants without game type, show "Just Spinner"
    if (result.type === 'participants') {
      return `: ${t('sidebar.games.justSpinner', 'Just Spinner')}`;
    }
    
    return '';
  };

  // Group results by date, then by spinner name, then by type
  const groupedByDateSpinnerAndType = results.reduce((acc, result) => {
    const date = new Date(result.timestamp);
    let dayLabel = '';
    if (isToday(date)) {
      dayLabel = t('sidebar.results.today', 'Today');
    } else if (isYesterday(date)) {
      dayLabel = t('sidebar.results.yesterday', 'Yesterday');
    } else {
      dayLabel = format(date, 'PPP');
    }
    
    const spinnerName = getSpinnerName(result);
    const gameTypeSuffix = getGameTypeSuffix(result);
    const fullSpinnerName = spinnerName + gameTypeSuffix;
    const typeKey = result.type;
    
          if (!acc[dayLabel]) acc[dayLabel] = {};
      if (!acc[dayLabel][fullSpinnerName]) acc[dayLabel][fullSpinnerName] = {};
      if (!acc[dayLabel][fullSpinnerName][typeKey]) acc[dayLabel][fullSpinnerName][typeKey] = [];
      acc[dayLabel][fullSpinnerName][typeKey].push(result);
    return acc;
  }, {} as Record<string, Record<string, Record<string, SpinResult[]>>>);

  // Map SidebarMode to i18n key
  const typeTitleMap: Record<string, string> = {
    participants: t('sidebar.participants', 'Participants'),
    numbers: t('sidebar.numbers', 'Numbers'),
    'random-numbers': t('sidebar.randomNumbers', 'Random Numbers'),
    colors: t('sidebar.colors', 'Colors'),
    binary: t('sidebar.binary', 'Binary'),
    dice: t('sidebar.dice', 'Dice'),
  };

  const getColorDisplayName = (hexOrKey: string) => {
    const color = colorOptions.find(c => c.name === hexOrKey || c.key === hexOrKey);
    return color ? t(`colors.${color.key}`) : hexOrKey;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
      contentLabel={t('sidebar.results.title', 'Spin Results')}
      style={{
        overlay: {
          zIndex: 10000,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        content: {
          position: 'relative',
          inset: 'auto',
          border: `1px solid ${currentTheme.border}`,
          background: currentTheme.paper,
          color: currentTheme.textPrimary,
          borderRadius: '8px',
          maxWidth: '600px',
          width: '90%',
          padding: '20px',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none', fontSize: 22, color: currentTheme.textPrimary, cursor: 'pointer', zIndex: 10 }}
        aria-label="Close"
      >
        &#10005;
      </button>
      <h2 className="text-lg font-bold mb-4" style={{ marginRight: 32 }}>{t('sidebar.results.title', 'Spin Results')}</h2>
      <div className="space-y-4 overflow-y-auto pr-2" style={{ flex: 1 }}>
        {Object.entries(groupedByDateSpinnerAndType).map(([day, spinnerGroups]) => (
          <div key={day} className="mb-6">
            {/* Date header - shown only once per day */}
            <div className="flex items-center mb-3">
              <span className="text-sm font-semibold" style={{ color: currentTheme.textSecondary }}>{day}</span>
              <div className="flex-grow border-t ml-3" style={{ borderColor: currentTheme.border }}></div>
            </div>
            
            {/* Spinner groups */}
            {Object.entries(spinnerGroups).map(([fullSpinnerName, typeGroups]) => (
              <div key={fullSpinnerName} className="mb-4 ml-2">
                {/* Spinner name header */}
                <div className="text-sm font-bold mb-2" style={{ color: currentTheme.textPrimary }}>
                  {fullSpinnerName}
                </div>
                
                {/* Type groups */}
                {Object.entries(typeGroups).map(([type, results]) => (
                  <div key={type} className="mb-3 ml-4">
                    {/* Type header */}
                    <div className="text-xs font-semibold mb-2" style={{ color: currentTheme.textPrimary }}>
                      {typeTitleMap[type] || type}
                    </div>
                    
                    {/* Results list */}
                    <div className="space-y-1">
                      {results.map((result) => (
                        <div key={result.id} className="flex items-center text-sm ml-4" style={{ minHeight: 20 }}>
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: currentTheme.textSecondary,
                              fontSize: result.type === 'dice' ? '1.5rem' : '0.875rem',
                              marginRight: 8,
                            }}
                          >
                            {result.type === 'colors' ? getColorDisplayName(result.winner) : result.winner}
                          </span>
                          <span className="text-xs text-gray-500" style={{ width: 80, textAlign: 'right', marginRight: 8 }}>
                            {format(result.timestamp, 'hh:mm:ss a')}
                          </span>
                          <span style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
                            {result.gameResultCandidate ? (
                              <span style={{ fontSize: '0.75rem' }}>
                                {result.gameResultCandidate === '🔘' ? (
                                  <span style={{ filter: 'brightness(1.2) contrast(1.1) saturate(0.7)' }}>⚪</span>
                                ) : (
                                  result.gameResultCandidate
                                )}
                              </span>
                            ) : (
                              // Show grey dot for non-game results (Just Spinner entries)
                              <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>⚪</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
      <button
        onClick={onClose}
        className="mt-6 w-full py-2 rounded bg-gray-200 dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
        style={{ border: 'none', outline: 'none' }}
      >
        {t('common.close', 'Close')}
      </button>
    </Modal>
  );
}; 